<template>
  <WrapperTitle :title="title">
    <Upload
      v-if="firstUpload"
      v-model:uploadFile="uploadFile"
      @handle-change="handleChange"
      accept="video/mp4"
      :tips="title"
      ref="uploadRef"
    ></Upload>
    <WrapperViewEditor
      v-else
      ref="viewWarper"
      v-model:isConvert="isConvert"
      v-model:isRun="isRun"
      :size="gifSize"
      @start-convert="startConvert"
      @stop="stop"
      @clean="clean"
      @download="download"
    >
      <template #left>
        <div class="w-full h-90">
          <video
            id="targetVideo"
            ref="targetVideo"
            class="w-full h-full object-contain"
            controls
            autoplay
            muted
            disablePictureInPicture
          ></video>
        </div>
        <TimeLine ref="timeLine" @reload="videoReload"></TimeLine>
      </template>
      <template #form>
        <FormNumber label="宽度" id="width" v-model="form.width" :min="1" :max="3000">
          <IconTooltip
            v-if="form.width > 1200"
            iconName="warning"
            info="宽度有点大会导致生成速度变长，降低一点"
            side="top"
            @handle-click="fixWidth"
          />
        </FormNumber>
        <FormNumber label="高度" id="height" v-model="form.height" :min="1" :max="3000">
          <IconTooltip
            v-if="form.height > 1200"
            iconName="warning"
            info="高度有点大会导致生成速度变长，降低一点"
            side="top"
            @handle-click="fixHeight"
          />
        </FormNumber>
        <FormSelect label="播放速度" id="speed" placeholder="请选择速度" v-model="form.speed" :options="speedOptions" />
        <FormNumber label="每秒帧数" id="fps" v-model="form.fps" :min="1" :max="30">
          <IconTooltip
            v-if="form.fps > 9"
            iconName="warning"
            info="每秒帧数过高会导致生成速度变慢哦，降低一点"
            side="top"
            @handle-click="fixFps"
          />
          <IconTooltip v-else iconName="info" info="每秒帧数越小则生成速度快、文件体积小" side="top" />
        </FormNumber>
        <FormSlider label="画质" id="maxColors" v-model="form.maxColors" :min="2" :max="255" :step="1">
          <IconTooltip iconName="info" info="画质2-255表示色块数（越大画质越高）" side="top" />
        </FormSlider>
      </template>
    </WrapperViewEditor>
  </WrapperTitle>
</template>

<script lang="ts" setup>
import type { UploadFile } from '~/types/upload';
import { Slider } from '~/components/ui/slider';
import type { MP4ArrayBuffer } from 'mp4box';
import { useVideoStoreHook } from '~/store/modules/video';
import { useFFmpegStoreHook } from '~/store/modules/ffmpeg';
import { useGifStoreHook } from '~/store/modules/gif';
import { reportError } from '~/utils/sentry';

definePageMeta({
  layout: 'default',
  title: '视频转GIF',
  svgIcon: 'video',
  keywords: '视频转GIF,gif转换,gif在线制作,在线视频转GIF的软件,视频转动图,视频怎么转gif',
  description: '视频转GIF',
  tag: 'stable',
  sort: 2,
});

const title = '视频转GIF';
const description = '视频转GIF';
const keywords = '视频转GIF,gif转换,gif在线制作,在线视频转GIF的软件,视频转动图,视频怎么转gif';
useSeoMeta({
  title: title,
  keywords: keywords,
  ogTitle: title,
  description: description,
  ogDescription: description,
});

const uploadFile = ref<UploadFile>();
const firstUpload = ref(true);
const targetVideo = ref();
const timeLine = ref();
const viewWarper = ref();
const uploadRef = ref();

const speedOptions = ref([
  { label: '0.5x', value: 0.5 },
  { label: '1x', value: 1 },
  { label: '1.25x', value: 1.25 },
  { label: '1.5x', value: 1.5 },
  { label: '2x', value: 2 },
  { label: '4x', value: 4 },
]);
const form = reactive({
  width: 300,
  height: 300,
  delay: 50,
  speed: 1,
  maxColors: 255,
  nbSamples: 0, // 视频总帧数
  fps: 6, // 每秒帧数
});
const gifSize = ref(0);
// 是否正在转换中
const isRun = ref(false);
// s是否转换完成了
const isConvert = ref(false);

// 真实时长(未修改)
const videoDuration = ref(0);
// 真实宽高(未修改)
const videoWidth = ref(600);
const videoHeight = ref(374);

//---------------------减少宽高------------------------
const fixWidth = () => {
  form.width = 960;
  form.height = Math.floor(videoHeight.value * (form.width / videoWidth.value));
};
const fixHeight = () => {
  form.height = 720;
  form.width = Math.floor(videoWidth.value * (form.height / videoHeight.value));
};
const fixFps = () => {
  form.fps = 6;
};

const ffmpegStore = useFFmpegStoreHook();

const videoReload = () => {
  targetVideo.value.src = `${uploadFile.value!.url}#t=${timeLine.value.leftTimePos()},${timeLine.value.rightTimePos()}`;
};
onMounted(() => {
  if (videoStore.videoFile) {
    uploadFile.value = videoStore.videoFile;
    handleChange();
  }
});

onUnmounted(() => {
  videoStore.setVideoFile(null);
  stop();
});

const handleChange = async () => {
  if (!uploadFile.value) return;
  firstUpload.value = false;
  isRun.value = true;
  await nextTick();

  videoStore.setVideoFile(uploadFile.value.url!);
  if (targetVideo.value) targetVideo.value.src = uploadFile.value!.url;
  const buffer = (await uploadFile.value!.raw!.arrayBuffer()) as MP4ArrayBuffer;
  await getVideoInfoWithWebCodec(buffer)
    .then((videoInfo) => {
      form.width = videoInfo.width;
      form.height = videoInfo.height;
      videoHeight.value = videoInfo.height;
      videoWidth.value = videoInfo.width;
      videoDuration.value = videoInfo.duration;
    })
    .catch(async (err) => {
      console.error(err);
      await getVideoInfoByVideoTag(uploadFile.value!.url!)
        .then((videoFileInfo) => {
          form.width = videoFileInfo.width;
          form.height = videoFileInfo.height;
          videoHeight.value = videoFileInfo.height;
          videoWidth.value = videoFileInfo.width;
          videoDuration.value = videoFileInfo.duration;
        })
        .catch(async (_err) => {
          viewWarper.value?.setProcessText('开始转码视频...');
          viewWarper.value?.progressInc(10, 6000);
          const progress = (e: any) => {
            // console.log('@', e.progress, e.time / 1000000);
            if (e.progress > 1 || e.progress <= 0) return;
            viewWarper.value?.setPercentage(Math.floor(e.progress * 100));
          };
          const blob = await ffmpegStore.transcode(uploadFile.value!.url!, progress);
          videoStore.setVideoFile(blob);
          uploadFile.value = videoStore.videoFile;
          if (targetVideo.value) targetVideo.value.src = uploadFile.value!.url;
          // 重新修改视频信息
          await getVideoInfoByVideoTag(uploadFile.value!.url!)
            .then((videoFileInfo) => {
              form.width = videoFileInfo.width;
              form.height = videoFileInfo.height;
              videoHeight.value = videoFileInfo.height;
              videoWidth.value = videoFileInfo.width;
              videoDuration.value = videoFileInfo.duration;
            })
            .catch((err) => {
              console.error(err);
              toast.error('当前浏览器不支持该视频编码，请更换浏览器！', {
                position: 'top-center',
                closeButton: false,
              });
            });
        });
    });
  viewWarper.value?.progressFinish();
  isRun.value = false;
  timeLine.value.initTime(videoDuration.value);
};

const videoStore = useVideoStoreHook();

//---------------------GIF生成------------------------
const gifStore = useGifStoreHook();

const startConvert = async () => {
  targetVideo.value.pause();
  await encodeGif()
    .then((blob) => {
      viewWarper.value?.progressFinish();
      isConvert.value = true;
      const url = URL.createObjectURL(blob);
      viewWarper.value?.showResult(url);
      gifSize.value = blob.size;
    })
    .catch((_e) => {
      toast.error('转换失败！您当前浏览器或版本不支持该功能，请更换浏览器！(推荐使用新版Chrome、Edge)', {
        position: 'top-center',
        closeButton: false,
      });
    });
  isRun.value = false;
};

const encodeGif = async () => {
  return new Promise<Blob>(async (resolve, reject) => {
    // 优先使用webcodec
    encodeWithWebCodec()
      .then((blob) => resolve(blob))
      .catch(async (err_webcodec) => {
        console.error(err_webcodec);
        // webcodec失败则使用video标签
        encodeWithVideoTag()
          .then((blob) => resolve(blob))
          .catch((err_tag) => {
            // console.error(err_tag);
            // 最后使用ffmpeg
            encodeWitFFmpeg()
              .then((blob) => resolve(blob))
              .catch((err_ffmpeg) => {
                reportError('encodeWithWebCodec', err_webcodec);
                reportError('encodeWithVideoTag', err_tag);
                reportError('encodeWitFFmpeg', err_ffmpeg);
                reject(err_ffmpeg);
              });
          });
      });
  });
};
const abortController = ref(new AbortController());
const encodeWitFFmpeg = async () => {
  return new Promise<Blob>(async (resolve, reject) => {
    try {
      abortController.value = new AbortController();
      viewWarper.value?.progressInit('尝试使用FFmpeg生成GIF...');
      const start = timeLine.value.leftTimePos();
      const end = timeLine.value.rightTimePos();
      const blob = await ffmpegStore.toGif(
        uploadFile.value!.url!,
        form.width,
        form.height,
        form.fps,
        form.speed,
        form.maxColors,
        start,
        end - start,
        abortController.value.signal,
        (e: any) => {
          if (e.progress > 1 || abortController.value.signal.aborted) return;
          viewWarper.value?.setPercentage(Math.floor(e.progress * 100));
        },
      );
      resolve(blob);
    } catch (error: any) {
      if (error.name === 'AbortError') {
        // 不返回任何值，表示用户主动取消
      } else reject(error);
    }
  });
};

const encodeWithWebCodec = () => {
  return new Promise<Blob>(async (resolve, reject) => {
    try {
      viewWarper.value?.progressInit();
      if (form.width > 1200 || form.height > 720) {
        toast('尺寸提示', {
          description: '生成GIF尺寸过大,需要较长时间生成,请耐心等待！',
          duration: 10000,
          classes: { title: 'font-bold' },
        });
      }
      viewWarper.value?.progressInc(10, 3000);
      const start = timeLine.value.leftTimePos();
      const end = timeLine.value.rightTimePos();
      const buffer = (await uploadFile.value!.raw!.arrayBuffer()) as MP4ArrayBuffer;
      const videoFrames = await decodeVideo(buffer, start, end, form.fps);
      const delay = Math.ceil(1e3 / form.fps / form.speed);
      const frames = videoFrames.map((frame) => ({
        data: frame.img,
        delay,
      }));
      if (frames.length > 60) {
        toast('帧图提示', {
          description: '提取帧图超过60张,需要较长时间生成,请耐心等待！',
          duration: 10000,
          classes: { title: 'font-bold' },
        });
      }
      const args = {
        width: form.width,
        height: form.height,
        maxColors: form.maxColors,
        frames,
      };
      const blob = await gifStore.createGif(args, function (percent: number, time: number, message: string) {
        viewWarper.value?.setProcessText(message);
        viewWarper.value?.progressInc(percent * 0.9, time);
      });
      resolve(blob);
    } catch (error) {
      reject(error);
    }
  });
};

const encodeWithVideoTag = () => {
  return new Promise<Blob>(async (resolve, reject) => {
    try {
      viewWarper.value?.progressInit('尝试使用video标签提取帧图...');
      const start = timeLine.value.leftTimePos();
      const end = timeLine.value.rightTimePos();
      const outPutFrameCount = Math.floor((end - start) * form.fps);
      const videoFrames = await getFramesByVideoTag(uploadFile.value!.url!, form.width, form.height, start, end, outPutFrameCount);
      const delay = Math.ceil(1e3 / form.fps / form.speed);
      const frames = videoFrames.map((frame) => ({
        data: frame,
        delay,
      }));
      const args = {
        width: form.width,
        height: form.height,
        maxColors: form.maxColors,
        frames,
      };
      const blob = await gifStore.createGif(args, function (percent: number, time: number, message: string) {
        viewWarper.value?.setProcessText(message);
        viewWarper.value?.progressInc(percent * 0.9, time);
      });
      resolve(blob);
    } catch (error) {
      reject(error);
    }
  });
};

const download = () => {};

const clean = () => {
  firstUpload.value = true;
  targetVideo.value.src = '';
};

const stop = () => {
  gifStore.terminal();
  abortController.value.abort();
};
</script>

<style lang="scss" scoped>
video {
  &::-webkit-media-controls {
    display: none !important;
  }
  &::-webkit-media-controls-volume-control-container {
    display: none !important;
  }
  &::-internal-media-controls-overflow-button {
    display: none !important;
  }
}
</style>
